import React, { useEffect, useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArcgisMap } from '../../pages/mapComponent';
import OverlayDropdown from '../../../components/uielements/overlayDropdown';
import LoadingScreenSecondary from '../../../components/loadingScreen/loadingScreen';
import useOverlayStore from '../../../store/overlayStore';
import {
  getAddressesCoordinates,
  getOverlayByAddressCoordinates,
  getOverlaysAplliedForAddress,
  checkCacheStatus,
} from '../../../services/getAddressList';

const MapWithOverlays = ({
  searchAddress,
  onError = () => {},
  className,
  showDropdown = true,
  mapProps = {},
  geoJsonCode,
}) => {
  const [addressLoading, setAddressLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cacheReady, setCacheReady] = useState(false);
  const [cacheCheckError, setCacheCheckError] = useState(null);
  const queryClient = useQueryClient();
  const pollingIntervalRef = useRef(null);
  const retryCountRef = useRef(0);

  // Configuration for polling
  const POLLING_CONFIG = {
    INITIAL_INTERVAL: 2000, // Start with 2 seconds
    MAX_INTERVAL: 10000, // Max 10 seconds between checks
    MAX_RETRIES: 30, // Maximum 30 attempts (up to 5 minutes total)
    BACKOFF_MULTIPLIER: 1.5, // Exponential backoff multiplier
  };

  const {
    selectedOverlayIds,
    bushFireOverlays,
    overlayOptions,
    isOverlayLoading,
    addressData,
    fetchingOverlayIds,
    addBushFireOverlay,
    addFetchingOverlayId,
    removeFetchingOverlayId,
    setOverlayOptions,
    setIsOverlayLoading,
    setOverlayError,
    setAddressData,
    handleOverlayChange,
  } = useOverlayStore();

  // Calculate next polling interval with exponential backoff
  const getNextInterval = (currentRetry) => {
    const interval = Math.min(
      POLLING_CONFIG.INITIAL_INTERVAL * Math.pow(POLLING_CONFIG.BACKOFF_MULTIPLIER, currentRetry),
      POLLING_CONFIG.MAX_INTERVAL
    );
    return Math.floor(interval);
  };

  // Check cache status with polling
  const checkCacheStatusWithPolling = async () => {
    if (!geoJsonCode) {
      setCacheReady(true);
      return;
    }

    try {
      const response = await checkCacheStatus({ geoJsonCode });

      if (response?.status === true) {
        // Cache is ready
        setCacheReady(true);
        setCacheCheckError(null);
        retryCountRef.current = 0;

        // Clear any existing polling
        if (pollingIntervalRef.current) {
          clearTimeout(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        return true;
      } else {
        // Cache not ready, continue polling
        retryCountRef.current += 1;

        if (retryCountRef.current >= POLLING_CONFIG.MAX_RETRIES) {
          // Max retries reached
          const errorMsg = 'Cache preparation timed out. Please try again later.';
          setCacheCheckError(errorMsg);
          if (onError) onError(errorMsg);
          return false;
        }

        // Schedule next check with exponential backoff
        const nextInterval = getNextInterval(retryCountRef.current);
        pollingIntervalRef.current = setTimeout(() => {
          checkCacheStatusWithPolling();
        }, nextInterval);

        return false;
      }
    } catch (error) {
      console.error('Cache status check error:', error);
      retryCountRef.current += 1;

      if (retryCountRef.current >= POLLING_CONFIG.MAX_RETRIES) {
        const errorMsg = 'Failed to verify cache status. Please try again.';
        setCacheCheckError(errorMsg);
        if (onError) onError(errorMsg);
        return false;
      }

      // Retry on error with backoff
      const nextInterval = getNextInterval(retryCountRef.current);
      pollingIntervalRef.current = setTimeout(() => {
        checkCacheStatusWithPolling();
      }, nextInterval);

      return false;
    }
  };

  // Initial cache check on mount or when geoJsonCode changes
  useEffect(() => {
    if (!geoJsonCode) {
      setCacheReady(true);
      return;
    }

    setCacheReady(false);
    setCacheCheckError(null);
    retryCountRef.current = 0;

    checkCacheStatusWithPolling();

    // Cleanup on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearTimeout(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [geoJsonCode]);

  // Fetch address coordinates only after cache is ready
  useEffect(() => {
    if (!searchAddress?.address || !cacheReady) return;
    fetchAddressCoordinates();
  }, [searchAddress?.address, cacheReady]);

  // Restore overlays on component mount or when selectedOverlayIds change
  useEffect(() => {
    if (!cacheReady) return;

    if (selectedOverlayIds.length > 0 && addressData?.geometry?.rings) {
      const missingOverlays = selectedOverlayIds.filter(
        (id) => !bushFireOverlays.some((overlay) => overlay.overlayId === id)
      );

      missingOverlays.forEach((overlayId) => {
        fetchOverlayByAddress(overlayId, addressData.geometry.rings);
      });
    }
  }, [selectedOverlayIds, addressData, cacheReady]);

  const fetchAddressCoordinates = async () => {
    setAddressLoading(true);

    try {
      const data = await getAddressesCoordinates({ geoJsonCode: geoJsonCode || '' });

      if (data?.geometry?.rings) {
        const updatedAddress = { ...searchAddress, geometry: data.geometry };
        setAddressData(updatedAddress);

        await fetchOverlaysOfProperty(data.geometry.rings);
      } else {
        const errorMsg = 'Unable to fetch address coordinates.';
        if (onError) onError(errorMsg);
      }
    } catch (error) {
      const errorMsg = 'Failed to load address coordinates.';
      if (onError) onError(errorMsg);
      console.error('Address coordinates error:', error);
    } finally {
      setAddressLoading(false);
    }
  };

  const fetchOverlayByAddress = async (overlayId, geometryRings) => {
    if (!geometryRings || !overlayId || !cacheReady) return;

    if (fetchingOverlayIds.includes(overlayId)) {
      return;
    }

    // Check if data is already in cache
    const cachedData = queryClient.getQueryData(['overlay', overlayId, geometryRings[0]]);

    if (cachedData) {
      addBushFireOverlay(cachedData);
      return;
    }

    setIsOverlayLoading(true);
    setOverlayError('');
    addFetchingOverlayId(overlayId);

    try {
      // Fetch using queryClient to leverage cache
      const data = await queryClient.fetchQuery({
        queryKey: ['overlay', overlayId, geometryRings[0]],
        queryFn: async () => {
          const result = await getOverlayByAddressCoordinates({
            overlayId: overlayId,
            geoJsonCode: geoJsonCode,
          });
          return result ? { ...result, overlayId } : null;
        },
        staleTime: 1000 * 60 * 10,
        cacheTime: 1000 * 60 * 30,
      });

      if (data) {
        addBushFireOverlay(data);
      } else {
        setOverlayError(`Unable to fetch data for overlay ${overlayId}`);
      }
    } catch (error) {
      setOverlayError(`Failed to load data for overlay ${overlayId}`);
      console.error('Overlay fetch error:', error);
    } finally {
      removeFetchingOverlayId(overlayId);
      setIsOverlayLoading(false);
    }
  };

  const fetchOverlaysOfProperty = async (geometryRings) => {
    if (!geometryRings || !cacheReady) return;

    setLoading(true);
    setIsOverlayLoading(true);

    try {
      const data = await getOverlaysAplliedForAddress({
        geoJsonCode,
      });

      setOverlayOptions(data || []);
    } catch (err) {
      console.error('Property overlays error:', err);
      if (onError)
        onError('An error occurred while fetching property overlays. Please try again later.');
    } finally {
      setLoading(false);
      setIsOverlayLoading(false);
    }
  };

  const onOverlayChange = async (selectedOverlays) => {
    if (!cacheReady) return;
    await handleOverlayChange(selectedOverlays, addressData, fetchOverlayByAddress);
  };

  const isLoading = !cacheReady || addressLoading || isOverlayLoading || loading;
  const showCacheWaitingMessage = !cacheReady && !cacheCheckError;

  return (
    <div className={className}>
      {isLoading && (
        <LoadingScreenSecondary
          message={showCacheWaitingMessage ? 'Getting everything ready for you.' : undefined}
        />
      )}

      {cacheCheckError && (
        <div
          className="cache-error-message"
          style={{
            padding: '1rem',
            backgroundColor: '#fee',
            color: '#c00',
            borderRadius: '4px',
            margin: '1rem 0',
          }}
        >
          {cacheCheckError}
        </div>
      )}

      {showDropdown && cacheReady && (
        <OverlayDropdown
          onOverlayChange={onOverlayChange}
          overlayOptions={overlayOptions || []}
          isDisabled={isLoading}
          selectedOverlays={selectedOverlayIds}
        />
      )}

      {!addressLoading && addressData && cacheReady && (
        <ArcgisMap
          address={addressData?.address}
          cords={addressData?.geometry?.rings}
          bushfireData={bushFireOverlays || []}
          aria-label="Map showing selected address location"
          role="region"
          {...mapProps}
          isAdmin={true}
        />
      )}
    </div>
  );
};

export default MapWithOverlays;
